import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  List,
  Typography,
  Divider,
  ListItemIcon,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NavItem from './NavItem';

const NavGroup = ({ item }) => {
  const menu = useSelector((state) => state?.menu);
  const drawerOpen = menu?.drawerOpen;

  const accordionRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  const navItems = item.children?.filter((child) => child.type === 'item') ?? [];

  if (navItems.length === 0) return null;

  const isSingleItem = navItems.length === 1;
  const IconComponent = item.icon;

  const gradientTextSx = {
    background: 'linear-gradient(90deg, #7e57c2, #512da8)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const handleAccordionChange = (_, isExpanded) => {
    setExpanded(isExpanded);

    if (isExpanded) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 300);
    }
  };

  const GroupHeader = ({ showArrow = false }) => (
    <Box
      sx={{
        px: drawerOpen ? 2.5 : 1.8,
        py: 1,
        borderRadius: 1.5,
        background: 'linear-gradient(135deg, #f3e7ff 0%, #e3d5ff 100%)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: `0 1px 3px ${alpha('#9c27b0', 0.12)}`,
        minHeight: 44,
        width: '100%',
      }}
    >
      {IconComponent && (
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            color: '#7e57c2',
            mr: drawerOpen ? 1.25 : 0,
            '& svg': {
              fontSize: '1.4rem',
            },
          }}
        >
          <IconComponent />
        </ListItemIcon>
      )}

      {drawerOpen && item.title && (
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            fontSize: '0.96rem',
            ...gradientTextSx,
          }}
        >
          {item.title}
        </Typography>
      )}

      {showArrow && drawerOpen && (
        <ExpandMoreIcon
          sx={{
            ml: 'auto',
            fontSize: 22,
            color: '#7e57c2',
            transition: 'transform .3s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      )}
    </Box>
  );

  if (isSingleItem) {
    return (
      <>
        <List disablePadding sx={{ mb: drawerOpen ? 1.25 : 0.5 }}>
          {drawerOpen && item.title && <GroupHeader />}
          <NavItem item={navItems[0]} level={1} />
        </List>

        <Divider
          sx={{
            borderColor: alpha('#d1c4e9', 0.6),
            borderBottomWidth: 1.5,
            my: drawerOpen ? 1 : 0.5,
            mx: drawerOpen ? 2 : 1.5,
          }}
        />
      </>
    );
  }

  return (
    <>
      <Accordion
        ref={accordionRef}
        expanded={expanded}
        onChange={handleAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          mb: drawerOpen ? 0.75 : 0.25,
          background: 'transparent',
          '&:before': {
            display: 'none',
          },
          '& .MuiAccordionSummary-root': {
            p: 0,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto',
            },
          },
          '& .MuiAccordionSummary-content': {
            m: 0,
            '&.Mui-expanded': {
              m: 0,
            },
          },
          '& .MuiAccordionDetails-root': {
            p: 0,
            pt: 0.5,
            pb: 1,
          },
        }}
      >
        <AccordionSummary>
          <GroupHeader showArrow />
        </AccordionSummary>

        <AccordionDetails>
          <List disablePadding>
            {navItems.map((menuItem) => (
              <NavItem
                key={menuItem.id}
                item={menuItem}
                level={1}
              />
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      <Divider
        sx={{
          borderColor: alpha('#d1c4e9', 0.6),
          borderBottomWidth: 1.5,
          my: drawerOpen ? 1 : 0.5,
          mx: drawerOpen ? 2 : 1.5,
        }}
      />
    </>
  );
};

NavGroup.propTypes = {
  item: PropTypes.object.isRequired,
};

export default NavGroup;